import { explainPuzzle } from "./explain.ts";
import {
  FEEDBACK_PHRASES,
  type Clue,
  type Feedback,
  type FeedbackKey,
  type Puzzle,
  type PuzzleDifficulty,
  type PuzzleOptions,
} from "./types.ts";

export const PUZZLE_VERSION = "v1" as const;
export const LAUNCH_DATE = "2026-09-07";
export const VARIED_CLUES_START_DATE = "2026-09-14";
export const DEFAULT_OPTIONS: PuzzleOptions = {
  length: 3,
  allowRepeatedDigits: false,
};

const SUPPORTED_FEEDBACK = new Map<number, FeedbackKey>([
  [0, "none"],
  [10, "oneWell"],
  [1, "oneWrong"],
  [20, "twoWell"],
  [2, "twoWrong"],
  [11, "oneWellOneWrong"],
]);

export function utcDateString(value: Date | string): string {
  if (typeof value === "string") {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      throw new Error(`Expected a UTC date in YYYY-MM-DD format, received "${value}".`);
    }

    const parsed = new Date(`${value}T00:00:00.000Z`);
    if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) {
      throw new Error(`Invalid calendar date "${value}".`);
    }

    return value;
  }

  if (Number.isNaN(value.getTime())) {
    throw new Error("Invalid date.");
  }

  return value.toISOString().slice(0, 10);
}

export function seedForDate(date: string): number {
  const input = `whats-the-code/${PUZZLE_VERSION}/${date}`;
  let hash = 0x811c9dc5;

  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }

  return hash >>> 0;
}

function createRandom(seed: number): () => number {
  let state = seed >>> 0;

  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  };
}

function shuffled<T>(values: readonly T[], random: () => number): T[] {
  const result = [...values];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    const current = result[index];
    const swap = result[swapIndex];
    if (current === undefined || swap === undefined) continue;
    result[index] = swap;
    result[swapIndex] = current;
  }
  return result;
}

export function enumerateCodes(options: PuzzleOptions = DEFAULT_OPTIONS): number[][] {
  const codes: number[][] = [];

  const visit = (prefix: number[]): void => {
    if (prefix.length === options.length) {
      codes.push(prefix);
      return;
    }

    for (let digit = 0; digit <= 9; digit += 1) {
      if (!options.allowRepeatedDigits && prefix.includes(digit)) continue;
      visit([...prefix, digit]);
    }
  };

  visit([]);
  return codes;
}

function feedbackCounts(
  code: readonly number[],
  guess: readonly number[],
  allowRepeatedDigits: boolean,
): { wellPlaced: number; wronglyPlaced: number } {
  if (code.length !== guess.length) {
    throw new Error("Code and guess must have the same length.");
  }

  let wellPlaced = 0;
  for (let index = 0; index < code.length; index += 1) {
    if (code[index] === guess[index]) wellPlaced += 1;
  }

  let shared = 0;
  if (!allowRepeatedDigits) {
    for (const guessDigit of guess) {
      if (code.includes(guessDigit)) shared += 1;
    }
  } else {
    for (let digit = 0; digit <= 9; digit += 1) {
      let codeCount = 0;
      let guessCount = 0;
      for (let index = 0; index < code.length; index += 1) {
        if (code[index] === digit) codeCount += 1;
        if (guess[index] === digit) guessCount += 1;
      }
      shared += Math.min(codeCount, guessCount);
    }
  }

  return { wellPlaced, wronglyPlaced: shared - wellPlaced };
}

function feedbackCode(
  code: readonly number[],
  guess: readonly number[],
  allowRepeatedDigits: boolean,
): number {
  const feedback = feedbackCounts(code, guess, allowRepeatedDigits);
  return feedback.wellPlaced * 10 + feedback.wronglyPlaced;
}

export function scoreGuess(
  code: readonly number[],
  guess: readonly number[],
  allowRepeatedDigits = false,
): Feedback {
  const { wellPlaced, wronglyPlaced } = feedbackCounts(code, guess, allowRepeatedDigits);
  const key = SUPPORTED_FEEDBACK.get(wellPlaced * 10 + wronglyPlaced);

  if (!key) {
    throw new Error(`Feedback ${wellPlaced}:${wronglyPlaced} has no banked phrase.`);
  }

  return {
    key,
    phrase: FEEDBACK_PHRASES[key],
    wellPlaced,
    wronglyPlaced,
  };
}

export function candidatesForClues(
  clues: readonly Clue[],
  options: PuzzleOptions = DEFAULT_OPTIONS,
): number[][] {
  return enumerateCodes(options).filter((candidate) =>
    clues.every(
      (clue) =>
        feedbackCode(candidate, clue.guess, options.allowRepeatedDigits) ===
        clue.feedback.wellPlaced * 10 + clue.feedback.wronglyPlaced,
    ),
  );
}

interface EvaluatedGuess {
  guess: number[];
  feedback: Feedback;
  remaining: number[][];
  tieBreak: number;
}

function generateClues(
  code: readonly number[],
  allCodes: readonly number[][],
  random: () => number,
  options: PuzzleOptions,
): Clue[] {
  const pool = shuffled(allCodes, random)
    .filter((guess) => guess.some((digit, index) => digit !== code[index]))
    .flatMap((guess) => {
      const signature = feedbackCode(code, guess, options.allowRepeatedDigits);
      if (!SUPPORTED_FEEDBACK.has(signature)) return [];
      return [{ guess, feedback: scoreGuess(code, guess, options.allowRepeatedDigits) }];
    });

  for (let attempt = 0; attempt < 48; attempt += 1) {
    let remaining = [...allCodes];
    const clues: Clue[] = [];
    const used = new Set<string>();
    const targetCounts = [180, 52, 14, 4, 2];

    for (let step = 0; step < 6; step += 1) {
      const evaluations: EvaluatedGuess[] = [];

      for (const item of pool) {
        const id = item.guess.join("");
        if (used.has(id)) continue;

        const signature = item.feedback.wellPlaced * 10 + item.feedback.wronglyPlaced;
        const next = remaining.filter(
          (candidate) =>
            feedbackCode(candidate, item.guess, options.allowRepeatedDigits) === signature,
        );

        if (next.length >= remaining.length || next.length === 0) continue;
        if (step < 3 && next.length === 1) continue;

        evaluations.push({
          ...item,
          remaining: next,
          tieBreak: random(),
        });
      }

      if (step >= 3) {
        const finishing = evaluations
          .filter((item) => item.remaining.length === 1)
          .sort((left, right) => left.tieBreak - right.tieBreak);
        const winner = finishing[0];
        if (winner) {
          clues.push({ guess: winner.guess, feedback: winner.feedback });
          return clues;
        }
      }

      const target = targetCounts[Math.min(step, targetCounts.length - 1)] ?? 2;
      const viable = evaluations
        .filter((item) => item.remaining.length > 1)
        .sort((left, right) => {
          const leftDistance = Math.abs(Math.log(left.remaining.length) - Math.log(target));
          const rightDistance = Math.abs(Math.log(right.remaining.length) - Math.log(target));
          return leftDistance - rightDistance || left.tieBreak - right.tieBreak;
        });

      const shortlist = viable.slice(0, Math.min(12, viable.length));
      const chosen = shortlist[Math.floor(random() * shortlist.length)];
      if (!chosen) break;

      clues.push({ guess: chosen.guess, feedback: chosen.feedback });
      remaining = chosen.remaining;
      used.add(chosen.guess.join(""));
    }
  }

  throw new Error(`Could not generate a unique puzzle for code ${code.join("")}.`);
}

function generateVariedClues(
  code: readonly number[],
  allCodes: readonly number[][],
  random: () => number,
  options: PuzzleOptions,
  date: string,
): Clue[] {
  const positiveKeys: FeedbackKey[] = ["oneWell", "oneWrong", "twoWell", "twoWrong", "oneWellOneWrong"];
  const includeNone = puzzleNumberForDate(date) % 3 !== 0;
  const pool = allCodes.flatMap((guess) => {
    const signature = feedbackCode(code, guess, options.allowRepeatedDigits);
    if (!SUPPORTED_FEEDBACK.has(signature)) return [];
    return [{ guess, feedback: scoreGuess(code, guess, options.allowRepeatedDigits) }];
  });

  for (let attempt = 0; attempt < 160; attempt += 1) {
    const length = 4 + Math.floor(random() * 3);
    const keys = shuffled(positiveKeys, random).slice(0, 3);
    if (includeNone) keys.push("none");
    while (keys.length < length) keys.push(positiveKeys[Math.floor(random() * positiveKeys.length)]!);
    const plan = shuffled(keys, random);
    const clues: Clue[] = [];
    let remaining = [...allCodes];

    for (const [step, key] of plan.entries()) {
      const evaluations: EvaluatedGuess[] = [];
      for (const item of pool) {
        if (item.feedback.key !== key || clues.some((clue) => clue.guess.every((digit) => item.guess.includes(digit)))) continue;
        const signature = item.feedback.wellPlaced * 10 + item.feedback.wronglyPlaced;
        const next = remaining.filter((candidate) => feedbackCode(candidate, item.guess, false) === signature);
        if (next.length === 0 || next.length >= remaining.length) continue;
        if (step === plan.length - 1 ? next.length !== 1 : next.length === 1) continue;
        evaluations.push({ ...item, remaining: next, tieBreak: random() });
      }
      const target = allCodes.length ** ((plan.length - step - 1) / plan.length);
      evaluations.sort((left, right) =>
        Math.abs(Math.log(left.remaining.length / target)) - Math.abs(Math.log(right.remaining.length / target)) || left.tieBreak - right.tieBreak,
      );
      const chosen = evaluations[Math.floor(random() * Math.min(16, evaluations.length))];
      if (!chosen) break;
      clues.push({ guess: chosen.guess, feedback: chosen.feedback });
      remaining = chosen.remaining;
    }

    if (clues.length !== length || remaining.length !== 1) continue;
    const essential = clues.every((_, removed) => allCodes.some((candidate) =>
      candidate.some((digit, index) => digit !== code[index]) && clues.every((clue, index) => index === removed ||
        feedbackCode(candidate, clue.guess, false) === clue.feedback.wellPlaced * 10 + clue.feedback.wronglyPlaced),
    ));
    if (essential && explainPuzzle({ clues }).code?.join("") === code.join("")) return clues;
  }
  throw new Error(`Could not generate varied clues for ${date}.`);
}

function calculateDifficulty(
  clues: readonly Clue[],
  allCodes: readonly number[][],
  options: PuzzleOptions,
): PuzzleDifficulty {
  let remaining = [...allCodes];
  const counts: number[] = [];

  for (const clue of clues) {
    const signature = clue.feedback.wellPlaced * 10 + clue.feedback.wronglyPlaced;
    remaining = remaining.filter(
      (candidate) =>
        feedbackCode(candidate, clue.guess, options.allowRepeatedDigits) === signature,
    );
    counts.push(remaining.length);
  }

  const earlyCandidates = counts[Math.min(1, counts.length - 1)] ?? allCodes.length;
  const score = Math.max(
    1,
    Math.min(5, Math.round(1.4 + Math.log2(Math.max(earlyCandidates, 1)) / 2.2)),
  );
  const label = score <= 2 ? "Easy" : score >= 4 ? "Hard" : "Medium";

  return { label, score, remainingAfterEachClue: counts };
}

export function puzzleNumberForDate(date: string): number {
  const launch = Date.parse(`${LAUNCH_DATE}T00:00:00.000Z`);
  const requested = Date.parse(`${date}T00:00:00.000Z`);
  return Math.floor((requested - launch) / 86_400_000) + 1;
}

export function codeForDate(value: Date | string): number[] {
  const date = utcDateString(value);
  const random = createRandom(seedForDate(date));
  const candidates = enumerateCodes();
  const code = candidates[Math.floor(random() * candidates.length)];
  if (!code) throw new Error("No code is available for this date.");
  return code;
}

export function generatePuzzle(
  value: Date | string,
  options: PuzzleOptions = DEFAULT_OPTIONS,
): Puzzle {
  const date = utcDateString(value);
  const seed = seedForDate(date);
  const random = createRandom(seed);
  const allCodes = enumerateCodes(options);
  const code = allCodes[Math.floor(random() * allCodes.length)];

  if (!code) {
    throw new Error("The configured puzzle has no possible codes.");
  }

  const varied = date >= VARIED_CLUES_START_DATE && options.length === 3 && !options.allowRepeatedDigits;
  const clues = varied ? generateVariedClues(code, allCodes, random, options, date) : generateClues(code, allCodes, random, options);
  return {
    version: varied ? "v2" : PUZZLE_VERSION,
    date,
    puzzleNumber: puzzleNumberForDate(date),
    seed,
    code,
    clues,
    difficulty: calculateDifficulty(clues, allCodes, options),
  };
}